import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Blobs, FloatingBubbles } from "../components/Effects";
import { ShieldCheck, Lock, Mail, Phone, Globe, FileText, CheckCircle2, AlertCircle, Clock } from "lucide-react";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  const handleBook = () => {
    navigate("/");
    setTimeout(() => {
      document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" });
    }, 200);
  };

  return (
    <div className="min-h-screen bg-[#FFF8F2] text-woofsy-dark overflow-hidden">
      <Navbar onBook={handleBook} />

      {/* Subtle Background Deco */}
      <Blobs />
      <FloatingBubbles count={10} />

      {/* Header Section */}
      <section className="relative pt-32 pb-10 px-5 sm:px-8 max-w-4xl mx-auto text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-4 text-xs sm:text-sm font-semibold text-woofsy-dark shadow-sm border border-woofsy-primary/20">
            <ShieldCheck className="w-4 h-4 text-woofsy-primary" />
            <span>Woofsy Data Trust & Compliance</span>
          </div>

          <h1 className="font-heading font-extrabold text-3xl sm:text-5xl text-woofsy-dark tracking-tight">
            Privacy <span className="text-gradient">Policy</span>
          </h1>

          <p className="mt-3 text-sm sm:text-base font-semibold text-woofsy-primary">
            Effective Date: July 31, 2026
          </p>

          <p className="mt-4 text-sm sm:text-base text-woofsy-dark/75 max-w-2xl mx-auto leading-relaxed">
            Welcome to Woofsy ("Woofsy", "Company", "we", "our", or "us"). At Woofsy, we value your trust and are committed to protecting your personal data.
          </p>
        </motion.div>
      </section>

      {/* Content Container */}
      <section className="py-8 px-5 sm:px-8 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="glass rounded-[2rem] p-7 sm:p-12 shadow-xl border border-white/80 space-y-8 text-woofsy-dark/85 text-sm sm:text-base leading-relaxed bg-white/90"
        >
          <div className="border-b border-slate-200/80 pb-6">
            <p className="text-woofsy-dark/80">
              This Privacy Policy explains how we collect, use, disclose, store, and protect your information when you use our website, mobile application (if applicable), or book our services. By accessing or using our services, you agree to this Privacy Policy.
            </p>
          </div>

          {/* Section 1 */}
          <div className="space-y-3">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">1.</span> Information We Collect
            </h2>
            <p>We may collect the following information:</p>

            <div className="grid sm:grid-cols-2 gap-4 pt-2">
              <div className="p-4 rounded-2xl bg-orange-50/60 border border-orange-100/80">
                <h3 className="font-heading font-bold text-sm text-woofsy-primary mb-2 flex items-center gap-1.5">
                  <FileText className="w-4 h-4" /> Personal Information
                </h3>
                <ul className="space-y-1.5 text-xs sm:text-sm text-woofsy-dark/80">
                  <li>• Full Name</li>
                  <li>• Mobile Number & Email Address</li>
                  <li>• Residential Address, City and State</li>
                  <li>• Payment details (processed securely by authorized payment partners)</li>
                </ul>
              </div>

              <div className="p-4 rounded-2xl bg-sky-50/60 border border-sky-100/80">
                <h3 className="font-heading font-bold text-sm text-sky-600 mb-2 flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4" /> Pet Information
                </h3>
                <ul className="space-y-1.5 text-xs sm:text-sm text-woofsy-dark/80">
                  <li>• Pet Name, Species, Breed, Age, Gender</li>
                  <li>• Vaccination details (if voluntarily shared)</li>
                  <li>• Medical conditions or allergies (if shared)</li>
                  <li>• Grooming preferences</li>
                </ul>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
              <h3 className="font-heading font-bold text-sm text-slate-700 mb-2">Technical Information</h3>
              <p className="text-xs sm:text-sm text-woofsy-dark/75">
                When you visit our website, we may automatically collect IP Address, Browser Type, Device Information, Operating System, Date and Time of Access, Cookies and Usage Data.
              </p>
            </div>
          </div>

          {/* Section 2 */}
          <div className="space-y-3">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">2.</span> Purpose of Data Collection
            </h2>
            <p>We collect and process your personal information for the following purposes:</p>
            <ul className="grid sm:grid-cols-2 gap-2 text-xs sm:text-sm pt-1">
              {[
                "To create and manage your bookings",
                "To provide doorstep pet grooming services",
                "To communicate confirmations & reminders",
                "To process payments securely",
                "To improve services & customer experience",
                "To respond to customer support requests",
                "To comply with legal & regulatory obligations",
                "To prevent fraud & platform misuse",
                "To send promotional updates (with consent)"
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 bg-slate-50/80 p-2.5 rounded-xl border border-slate-100">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 3 */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">3.</span> Lawful Basis for Processing
            </h2>
            <p className="text-xs sm:text-sm">
              We process your personal data only where permitted under applicable Indian law, including: your consent, performance of a contract, compliance with legal obligations, and legitimate business purposes that do not override your rights.
            </p>
          </div>

          {/* Section 4 */}
          <div className="space-y-3">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">4.</span> Sharing of Information
            </h2>
            <p className="font-semibold text-woofsy-primary text-sm">We do not sell your personal information.</p>
            <p className="text-xs sm:text-sm">Your information may be shared only with:</p>
            <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm text-woofsy-dark/80">
              <li>Authorized Woofsy grooming professionals</li>
              <li>Payment gateway providers & cloud hosting partners</li>
              <li>Customer support partners</li>
              <li>Government authorities or regulatory agencies where required by law</li>
            </ul>
            <p className="text-xs text-slate-500 italic">All third parties are required to maintain appropriate confidentiality and security measures.</p>
          </div>

          {/* Section 5 */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">5.</span> Cookies
            </h2>
            <p className="text-xs sm:text-sm">
              Our website uses cookies to remember user preferences, improve website functionality, measure performance, and enhance visitor experience. You may disable cookies through your browser settings, though some website features may not function properly if disabled.
            </p>
          </div>

          {/* Section 6 */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">6.</span> Data Security
            </h2>
            <p className="text-xs sm:text-sm">
              We maintain reasonable technical, organizational, and administrative safeguards to protect your personal data against unauthorized access, alteration, disclosure, loss, or misuse. Although we implement industry-standard security measures, no method of electronic transmission is 100% secure.
            </p>
          </div>

          {/* Section 7 */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">7.</span> Data Retention
            </h2>
            <p className="text-xs sm:text-sm">
              We retain your personal information only for as long as necessary to deliver our services, maintain business records, resolve disputes, and comply with legal, tax, or regulatory obligations. When no longer required, information is securely deleted or anonymized.
            </p>
          </div>

          {/* Section 8 */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">8.</span> Your Rights
            </h2>
            <p className="text-xs sm:text-sm">
              Subject to applicable law, you have the right to access, correct, or update your personal data, withdraw consent, request deletion of data, opt out of marketing communications, or submit a grievance regarding processing.
            </p>
          </div>

          {/* Section 9 */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">9.</span> Children's Privacy
            </h2>
            <p className="text-xs sm:text-sm">
              Our services are intended for individuals who are at least 18 years of age or who use our services under parent/guardian supervision. We do not knowingly collect personal information directly from children.
            </p>
          </div>

          {/* Section 10 */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">10.</span> Third-Party Services
            </h2>
            <p className="text-xs sm:text-sm">
              Our website may contain links to third-party websites or services (such as payment gateways or maps). We are not responsible for the privacy practices of third parties and encourage users to review their respective policies.
            </p>
          </div>

          {/* Section 11 */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">11.</span> Changes to This Privacy Policy
            </h2>
            <p className="text-xs sm:text-sm">
              We reserve the right to update this Privacy Policy at any time to reflect changes in our services, legal requirements, or business practices. Changes become effective upon publication on our website.
            </p>
          </div>

          {/* Section 12 - Grievance Officer */}
          <div className="p-6 rounded-3xl bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-200 space-y-3">
            <h2 className="font-heading font-bold text-lg text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">12.</span> Grievance Officer
            </h2>
            <p className="text-xs sm:text-sm text-woofsy-dark/80">
              In accordance with applicable Indian laws, including the Information Technology Act, 2000, Woofsy has appointed a Grievance Officer.
            </p>
            <div className="text-xs sm:text-sm space-y-1 font-medium pt-1">
              <p className="font-bold text-woofsy-dark">Grievance Officer — Woofsy</p>
              <p className="flex items-center gap-2 text-woofsy-primary"><Mail className="w-4 h-4" /> info@woofsy.co.in</p>
              <p className="flex items-center gap-2 text-slate-600"><Clock className="w-4 h-4" /> Response Time: Within 15 business days</p>
            </div>
          </div>

          {/* Section 13 - Contact Us */}
          <div className="p-6 rounded-3xl bg-sky-50/80 border border-sky-100 space-y-3">
            <h2 className="font-heading font-bold text-lg text-woofsy-dark flex items-center gap-2">
              <span className="text-sky-600 font-black">13.</span> Contact Us
            </h2>
            <p className="text-xs sm:text-sm text-woofsy-dark/80">
              For questions regarding this Privacy Policy or your personal data, please contact us:
            </p>
            <div className="grid sm:grid-cols-3 gap-3 text-xs sm:text-sm pt-1">
              <a href="mailto:info@woofsy.co.in" className="flex items-center gap-2 p-3 rounded-2xl bg-white border border-sky-200/60 hover:text-sky-600 transition-colors">
                <Mail className="w-4 h-4 text-sky-500" />
                <span>info@woofsy.co.in</span>
              </a>
              <a href="tel:+918421114134" className="flex items-center gap-2 p-3 rounded-2xl bg-white border border-sky-200/60 hover:text-sky-600 transition-colors">
                <Phone className="w-4 h-4 text-sky-500" />
                <span>+91-8421114134</span>
              </a>
              <a href="https://www.woofsy.co.in" target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 rounded-2xl bg-white border border-sky-200/60 hover:text-sky-600 transition-colors">
                <Globe className="w-4 h-4 text-sky-500" />
                <span>www.woofsy.co.in</span>
              </a>
            </div>
          </div>

          {/* Section 14 & Consent */}
          <div className="space-y-3 border-t border-slate-200/80 pt-6">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">14.</span> Governing Law
            </h2>
            <p className="text-xs sm:text-sm">
              This Privacy Policy shall be governed by and construed in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the competent courts located in Pune, Maharashtra, India.
            </p>

            <div className="p-4 rounded-2xl bg-emerald-50/80 border border-emerald-200 text-xs sm:text-sm text-emerald-900 font-semibold flex items-center gap-2 mt-4">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
              <span>Consent: By accessing or using Woofsy's website or services, you acknowledge that you have read, understood, and agree to this Privacy Policy.</span>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer onBook={handleBook} />
    </div>
  );
}
